import React, { useState, useRef } from 'react';
import { imageStorage, compressImage } from '../utils/imageStorage.js';

const EnhancedImageUpload = ({ value, onChange, disabled = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef(null);

  // Sync with external value changes
  React.useEffect(() => {
    setPreviewUrl(value || '');
  }, [value]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0 || disabled) return;
    
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF, etc.)');
      return;
    }

    // Validate file size (max 10MB original)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image file is too large. Please select an image smaller than 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 20, 90));
      }, 100);

      // Compress the image
      const compressedData = await compressImage(file, 350, 250, 0.7);
      
      // Clear progress interval
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Check final size
      const finalSize = compressedData.length * 0.75; // Estimate actual bytes
      console.log(`Original: ${(file.size / 1024).toFixed(1)}KB → Compressed: ${(finalSize / 1024).toFixed(1)}KB`);
      
      if (finalSize > 200000) { // 200KB limit
        alert(`Image is still too large after compression (${(finalSize / 1024).toFixed(1)}KB). Please try a smaller image or use one from our collection.`);
        setIsUploading(false);
        setUploadProgress(0);
        return;
      }

      // Upload to server instead of storing locally
      const formData = new FormData();
      // Convert base64 data URL to blob
      const response = await fetch(compressedData);
      const blob = await response.blob();
      formData.append('file', blob, `fish-${Date.now()}.jpg`);
      formData.append('fishId', 'admin-upload');
      formData.append('imageName', `fish-${Date.now()}.jpg`);

      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
      const uploadResponse = await fetch(`${apiBase}/api/images/upload`, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json();
      const finalUrl = uploadResult.url;

      setPreviewUrl(finalUrl);
      onChange(finalUrl);
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to process image. Please try again or choose a different image.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
  };

  const handleRemove = async () => {
    console.log('Removing image...');
    
    // If it's a Supabase URL, delete from Supabase storage
    if (value && value.includes('supabase')) {
      try {
        // Extract the path from the Supabase URL
        const url = new URL(value);
        const pathParts = url.pathname.split('/');
        // Find the bucket name and construct the key
        const bucketIndex = pathParts.findIndex(part => part === 'images' || part === process.env.SUPABASE_BUCKET || 'images');
        if (bucketIndex !== -1) {
          const key = pathParts.slice(bucketIndex + 1).join('/');
          const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
          await fetch(`${apiBase}/api/images?key=${encodeURIComponent(key)}`, {
            method: 'DELETE'
          });
        }
      } catch (error) {
        console.error('Failed to delete image from storage:', error);
      }
    }
    // If it's an IndexedDB reference, delete from IndexedDB (fallback for old images)
    else if (value && value.startsWith('indexeddb://')) {
      const imageId = value.replace('indexeddb://', '');
      imageStorage.deleteImage(imageId).catch(console.error);
    }
    
    setPreviewUrl('');
    onChange('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  // Get display URL (handle IndexedDB references for backward compatibility)
  const getDisplayUrl = async (url) => {
    if (!url) return '';
    if (url.startsWith('indexeddb://')) {
      const imageId = url.replace('indexeddb://', '');
      try {
        return await imageStorage.getImage(imageId);
      } catch (error) {
        console.error('Failed to load image from IndexedDB:', error);
        return '';
      }
    }
    // For Supabase URLs and other direct URLs, return as-is
    return url;
  };

  // Enhanced preview component
  const ImagePreview = ({ url }) => {
    const [displayUrl, setDisplayUrl] = useState('');
    
    React.useEffect(() => {
      if (url) {
        getDisplayUrl(url).then(setDisplayUrl);
      } else {
        setDisplayUrl('');
      }
    }, [url]);

    if (!displayUrl) return null;

    return (
      <img 
        src={displayUrl} 
        alt="Preview"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '8px'
        }}
      />
    );
  };

  return (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
        Fish Image
      </label>
      
      {/* Preview */}
      {previewUrl && (
        <div style={{ 
          position: 'relative', 
          marginBottom: '15px', 
          width: '200px', 
          height: '150px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '2px solid var(--border)'
        }}>
          <ImagePreview url={previewUrl} />
          <button
            type="button"
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'var(--coral)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        style={{
          border: `2px dashed ${dragActive ? 'var(--ocean-light)' : 'var(--border)'}`,
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center',
          background: dragActive ? 'var(--muted)' : 'var(--background)',
          transition: 'all 0.3s ease',
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
        onClick={() => !disabled && !isUploading && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !isUploading) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          onChange={handleChange}
          accept="image/*"
          style={{ display: 'none' }}
          disabled={disabled || isUploading}
        />
        
        {isUploading ? (
          <div>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>📤</div>
            <p style={{ margin: '0 0 10px 0' }}>Uploading...</p>
            <div style={{
              width: '100%',
              height: '8px',
              background: 'var(--muted)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                background: 'var(--ocean-light)',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>{uploadProgress}%</p>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '32px', marginBottom: '15px' }}>
              {dragActive ? '📥' : '🖼️'}
            </div>
            <p style={{ 
              margin: '0 0 10px 0', 
              fontWeight: 'bold',
              color: 'var(--foreground)'
            }}>
              {dragActive ? 'Drop image here' : 'Upload Custom Image'}
            </p>
            <p style={{ 
              margin: 0, 
              fontSize: '14px', 
              color: 'var(--muted-foreground)' 
            }}>
              Click here or drag & drop your fish image
            </p>
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '12px', 
              color: 'var(--muted-foreground)',
              fontStyle: 'italic'
            }}>
              Supports JPG, PNG, GIF • Max 10MB • Auto-compressed
            </p>
          </div>
        )}
      </div>

      {/* Storage Info */}
      <StorageInfo />
    </div>
  );
};

// Component to show storage usage
const StorageInfo = () => {
  const [storageInfo, setStorageInfo] = useState(null);

  React.useEffect(() => {
    imageStorage.getStorageInfo().then(info => {
      if (info.quota > 0) {
        setStorageInfo(info);
      }
    });
  }, []);

  if (!storageInfo || storageInfo.quota === 0) return null;

  const usagePercent = ((storageInfo.usage / storageInfo.quota) * 100).toFixed(1);
  const usageMB = (storageInfo.usage / (1024 * 1024)).toFixed(1);
  const quotaMB = (storageInfo.quota / (1024 * 1024)).toFixed(0);

  return (
    <div style={{
      fontSize: '11px',
      color: 'var(--muted-foreground)',
      marginTop: '8px',
      padding: '8px',
      background: 'var(--muted)',
      borderRadius: '4px'
    }}>
      📊 Storage: {usageMB}MB / {quotaMB}MB ({usagePercent}% used)
    </div>
  );
};

export default EnhancedImageUpload;